import { api } from '../../../services/api';

/**
 * Download a file (GLB or 3MF) for a model
 * @param modelId - The model ID
 * @param filename - The original filename
 * @param fileType - The file type to download ('glb' or 'three_mf')
 * @param extension - The file extension to use
 * @param t - Translation function
 */
export const downloadFile = async (
  modelId: string,
  filename: string,
  fileType: 'glb' | 'three_mf',
  extension: string,
  t: (key: string, options?: any) => string
): Promise<void> => {
  try {
    console.log(`Downloading ${fileType} for file: ${modelId}`);
    const freshUrl = await api.files.getDownloadUrl(modelId, fileType);
    const link = document.createElement('a');
    link.href = freshUrl;
    link.download = filename.replace(/\.[^/.]+$/, "") + extension;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error(`Error downloading ${fileType}:`, error);
    alert(t('gcodeGeneration.errors.downloadFailed', { fileType: fileType.toUpperCase() }));
  }
};

/**
 * Handle general loading errors
 * @param setError - Error state setter
 * @param setLoading - Loading state setter
 * @param message - Optional error message
 */
export const handleError = (
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>,
  message: string = "Failed to load 3D model"
): void => {
  setError(message);
  setLoading(false);
};

/**
 * Handle Canvas-specific errors
 * @param event - Canvas error event
 * @param setError - Error state setter
 * @param setLoading - Loading state setter
 */
export const handleCanvasError = (
  event: any,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
): void => {
  console.error("Canvas error caught:", event);
  setError("Failed to initialize 3D viewer");
  setLoading(false);
};

/**
 * Create WebGL context error handler
 * @param setError - Error state setter
 * @param setLoading - Loading state setter
 * @returns The error handler function
 */
export const createWebGLContextErrorHandler = (
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
) => {
  return (event: ErrorEvent) => {
    if (event.message.includes('WebGL') || event.message.includes('context lost') || event.message.includes('Could not load')) {
      console.warn('WebGL context or loading error intercepted:', event.message);
      // Don't set error state for all cards, just let this one fail quietly
      setLoading(false);
      setError("Failed to load 3D model");
    }
  };
}; 